/**
 * Live inbox wiring: `/api/v1/agent/*` against the configured API gateway
 * (`VITE_API_BASE_URL`), falling back to a same-origin path for Vite’s
 * `VITE_PROXY_API_TARGET` in dev. Never an upstream service URL — only the gateway.
 *
 * Workspace scope: values sent as `X-Workspace-Id` must come from the app’s active workspace
 * (WorkspaceContext / `/me` / workspace list) — Keycloak-aligned client ids, not user-typed strings.
 * Human-agent enforces token `azp` / `resource_access` against this header (docs/SECURITY.md §2.6).
 */

const WORKSPACE_HEADER = "X-Workspace-Id";

/**
 * Last workspace id synced from the UI (see WorkspaceProvider).
 * @type {string | null}
 */
let _inboxWorkspaceId = null;

/**
 * Keeps inbox fetches aligned with the active workspace when callers omit `workspaceId`.
 * Called from `WorkspaceContext` whenever `activeWorkspaceId` changes (or cleared on logout).
 *
 * @param {string | null} id
 */
export function setInboxAgentWorkspaceId(id) {
  _inboxWorkspaceId = id && String(id).trim() ? String(id).trim() : null;
}

/** @returns {string | null} Value last passed to {@link setInboxAgentWorkspaceId}. */
export function getInboxAgentWorkspaceId() {
  return _inboxWorkspaceId;
}

/**
 * When true, inbox list/navigation use the remote API (still same-origin paths below).
 * Controlled only by `VITE_USE_HUMAN_AGENT_INBOX` (see repo `.env.example`).
 */
export function isHumanAgentInboxLive() {
  const flag = import.meta.env?.VITE_USE_HUMAN_AGENT_INBOX;
  return flag === "true" || flag === "1";
}

/** Default silent inbox list poll when env is unset (60s safety net). Set `VITE_INBOX_LIST_POLL_MS=0` to disable. */
export const INBOX_LIST_POLL_MS_DEFAULT = 60_000;

/**
 * Background inbox list poll interval (ms).
 * Unset env → {@link INBOX_LIST_POLL_MS_DEFAULT}. `0` explicitly disables polling.
 */
export function inboxListPollIntervalMs() {
  const raw = import.meta.env?.VITE_INBOX_LIST_POLL_MS;
  if (raw === undefined || raw === null || String(raw).trim() === "") {
    return INBOX_LIST_POLL_MS_DEFAULT;
  }
  const n = Number.parseInt(String(raw).trim(), 10);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return n;
}

/**
 * @param {string} path e.g. "/inbox/conversations"
 * @returns {string} Absolute gateway URL when `VITE_API_BASE_URL` is set, else a relative
 * path for Vite's dev proxy. The dashboard origin serves only static assets in deployed
 * environments, so a relative path there reaches nginx (404/405) instead of the API.
 */
export function humanAgentInboxUrl(path) {
  const p = path.startsWith("/") ? path : `/${path}`;
  const relative = `/api/v1/agent${p}`;
  const base = import.meta.env?.VITE_API_BASE_URL;
  if (!base) return relative;
  return new URL(relative, base).toString();
}

/**
 * `X-Workspace-Id` for inbox requests (must be a Keycloak-backed workspace id from context, not arbitrary input).
 *
 * Resolution order:
 * 1. Non-empty `workspaceId` argument → use that.
 * 2. Else → use the module store from {@link setInboxAgentWorkspaceId} (active workspace from `WorkspaceContext`).
 * 3. If still empty → `{}` (no header).
 *
 * Most call sites pass `activeWorkspaceId` explicitly; the store is the fallback for code that does not.
 *
 * @param {string | null | undefined} workspaceId
 * @returns {Record<string, string>}
 */
export function inboxWorkspaceHeaders(workspaceId) {
  const wid =
    workspaceId != null && String(workspaceId).trim() !== ""
      ? String(workspaceId).trim()
      : _inboxWorkspaceId;
  if (!wid) return {};
  return { [WORKSPACE_HEADER]: wid };
}
